import React, { useState, useEffect, useRef } from 'react';
import { FaTrash, FaRedo, FaEdit, FaSave, FaTimes, FaImage, FaTag, FaMapMarkerAlt, FaCalendarAlt, FaCrop } from 'react-icons/fa';
import localPlaceholder from '../images/def_user_icon.svg';
import Input from './Input';
import Textarea from './Textarea';
import { useTheme } from '../contexts/ThemeContext';
import { formatListingPrice } from './moderation/utils';

const AdDetailsModalContent = ({ adData, categories, isEditing, setIsEditing, onSave }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    // Image editing state
    const [imageEdits, setImageEdits] = useState(() => ({
        main_image: { rotation: 0, is_deleted: false, crop: null },
        additional_images: (adData?.image_urls || []).map(url => ({
            url,
            rotation: 0,
            is_deleted: false,
            crop: null
        })),
    }));

    const [mainNewImage, setMainNewImage] = useState(null);
    const [additionalNewImages, setAdditionalNewImages] = useState([]);
    const [selectedImageIndex, setSelectedImageIndex] = useState(null);
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    
    // Crop state
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [cropImageData, setCropImageData] = useState({ url: null, isMain: false, originalUrl: null, blob: null });
    const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [cropPreview, setCropPreview] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const mainFileInputRef = useRef(null);
    const additionalFilesInputRef = useRef(null);
    const cropImageRef = useRef(null);
    const cropCanvasRef = useRef(null);

    // Editable fields state (AdminAnnouncementUpdateRequest)
    const [editableFields, setEditableFields] = useState({
        status: adData?.status || "pending",
        reject_message: adData?.reject_message ?? "",
        title: adData?.title || "",
        description: adData?.description || "",
        price: adData?.price != null ? String(adData.price) : "",
        currency: (adData?.currency || "").slice(0, 3),
        category_id: adData?.category_id ?? "",
        subcategory_id: adData?.subcategory_id ?? "",
        user_location_id: adData?.user_location_id != null ? adData.user_location_id : "",
        video_url: adData?.video_url ?? "",
        is_vip: !!adData?.is_vip,
        barter: !!adData?.barter,
        negotiable: !!adData?.negotiable,
        keywords: Array.isArray(adData?.keywords) ? adData.keywords.join(", ") : (adData?.keywords ? String(adData.keywords) : ""),
        measurement_unit: adData?.measurement_unit ?? "",
    });

    // Reset state when adData changes
    useEffect(() => {
        if (adData) {
            setEditableFields({
                status: adData.status || "pending",
                reject_message: adData.reject_message ?? "",
                title: adData.title || "",
                description: adData.description || "",
                price: adData.price != null ? String(adData.price) : "",
                currency: (adData.currency || "").slice(0, 3),
                category_id: adData.category_id ?? "",
                subcategory_id: adData.subcategory_id ?? "",
                user_location_id: adData.user_location_id != null ? adData.user_location_id : "",
                video_url: adData.video_url ?? "",
                is_vip: !!adData.is_vip,
                barter: !!adData.barter,
                negotiable: !!adData.negotiable,
                keywords: Array.isArray(adData.keywords) ? adData.keywords.join(", ") : (adData.keywords ? String(adData.keywords) : ""),
                measurement_unit: adData.measurement_unit ?? "",
            });
            setImageEdits({
                main_image: { rotation: 0, is_deleted: false, crop: null },
                additional_images: (adData.image_urls || []).map(url => ({
                    url,
                    rotation: 0,
                    is_deleted: false,
                    crop: null
                })),
            });
            setCropPreview(null);
            setMainNewImage(null);
            setAdditionalNewImages([]);
        }
    }, [adData]);

    // Helper to get category name
    const getCategoryName = (categoryId) => {
        if (!categories || !categoryId) return "Не указано";
        const category = categories.find(cat => cat.id === categoryId);
        return category ? (category.name_kz || category.name_ru || category.name) : "Неизвестно";
    };

    // Helper to get subcategory name
    const getSubcategoryName = (categoryId, subcategoryId) => {
        if (!categories || !categoryId || !subcategoryId) return "Не указано";
        const category = categories.find(cat => cat.id === categoryId);
        if (category && category.subcategories) {
            const subcategory = category.subcategories.find(subcat => subcat.id === subcategoryId);
            return subcategory ? (subcategory.name_kz || subcategory.name_ru || subcategory.name) : "Неизвестно";
        }
        return "Неизвестно";
    };

    // Image rotation handler
    const handleRotate = (url, isMain = false) => {
        if (isMain) {
            setImageEdits(prev => ({
                ...prev,
                main_image: {
                    ...prev.main_image,
                    rotation: (prev.main_image.rotation + 90) % 360,
                }
            }));
        } else {
            setImageEdits(prev => ({
                ...prev,
                additional_images: prev.additional_images.map(img =>
                    img.url === url ? { ...img, rotation: (img.rotation + 90) % 360 } : img
                ),
            }));
        }
    };

    // Image delete toggle handler
    const handleDeleteToggle = (url, isMain = false) => {
        if (isMain) {
            setImageEdits(prev => ({
                ...prev,
                main_image: {
                    ...prev.main_image,
                    is_deleted: !prev.main_image.is_deleted,
                }
            }));
        } else {
            setImageEdits(prev => ({
                ...prev,
                additional_images: prev.additional_images.map(img =>
                    img.url === url ? { ...img, is_deleted: !img.is_deleted } : img
                ),
            }));
        }
    };

    // Open crop modal
    const handleOpenCrop = async (url, isMain = false) => {
        try {
            // Always fetch image as blob to avoid CORS issues
            let blobUrl = null;
            let imageBlob = null;
            
            try {
                // Try to fetch with CORS first
                const response = await fetch(url, { 
                    mode: 'cors',
                    credentials: 'omit'
                });
                if (response.ok) {
                    imageBlob = await response.blob();
                    blobUrl = URL.createObjectURL(imageBlob);
                }
            } catch (fetchError) {
                console.warn('CORS fetch failed, trying without CORS:', fetchError);
                // If CORS fails, try without CORS mode (may work for same-origin)
                try {
                    const response = await fetch(url, { mode: 'no-cors' });
                    if (response.ok || response.type === 'opaque') {
                        imageBlob = await response.blob();
                        blobUrl = URL.createObjectURL(imageBlob);
                    }
                } catch (error2) {
                    console.warn('Failed to fetch image as blob:', error2);
                }
            }
            
            // Use blob URL if available, otherwise use original URL
            const finalUrl = blobUrl || url;
            setCropImageData({ 
                url: finalUrl, 
                isMain, 
                originalUrl: url,
                blob: imageBlob
            });
            setCropModalOpen(true);
            setCropPreview(null);
            setImageSize({ width: 0, height: 0 }); // Reset image size
            setCropArea({ x: 0, y: 0, width: 0, height: 0 }); // Reset crop area
        } catch (error) {
            console.error('Error loading image for crop:', error);
            // Fallback to original URL
            setCropImageData({ url, isMain, originalUrl: url, blob: null });
            setCropModalOpen(true);
            setCropPreview(null);
            setImageSize({ width: 0, height: 0 });
            setCropArea({ x: 0, y: 0, width: 0, height: 0 });
        }
    };

    // Initialize crop area when image loads
    useEffect(() => {
        if (!cropModalOpen || !cropImageData.url) {
            setImageSize({ width: 0, height: 0 });
            setCropArea({ x: 0, y: 0, width: 0, height: 0 });
            return;
        }

        // Use a small delay to ensure the ref is available
        const timer = setTimeout(() => {
            const img = cropImageRef.current;
            if (!img) {
                console.warn('Crop image ref not available');
                return;
            }
            
            // If we have a blob, use it directly (no CORS issues)
            // Otherwise, try to set crossOrigin
            if (cropImageData.blob) {
                // Blob URL doesn't need crossOrigin
                img.crossOrigin = null;
            } else if (!cropImageData.url.startsWith('blob:')) {
                // Try crossOrigin for regular URLs
                img.crossOrigin = 'anonymous';
            }
            
            const handleLoad = () => {
                if (!img.naturalWidth || !img.naturalHeight) {
                    console.warn('Image dimensions are zero');
                    return;
                }
                
                const maxWidth = 800;
                const maxHeight = 600;
                let width = img.naturalWidth;
                let height = img.naturalHeight;
                
                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width = width * ratio;
                    height = height * ratio;
                }
                
                setImageSize({ width, height });
                setCropArea({
                    x: width * 0.1,
                    y: height * 0.1,
                    width: width * 0.8,
                    height: height * 0.8
                });
            };

            // Set image source
            if (img.src !== cropImageData.url) {
                img.src = cropImageData.url;
            }
            
            if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
                handleLoad();
            } else {
                img.onload = handleLoad;
                img.onerror = (e) => {
                    console.warn('Image failed to load:', e, cropImageData.url);
                    // If image fails to load, try original URL
                    if (cropImageData.originalUrl && cropImageData.originalUrl !== img.src) {
                        img.crossOrigin = null;
                        img.src = cropImageData.originalUrl;
                    }
                };
            }
        }, 50);

        return () => {
            clearTimeout(timer);
        };
    }, [cropModalOpen, cropImageData.url, cropImageData.originalUrl, cropImageData.blob]);

    // Handle crop area dragging
    const handleCropMouseDown = (e) => {
        setIsDragging(true);
        const rect = cropImageRef.current?.getBoundingClientRect();
        if (rect) {
            setDragStart({
                x: e.clientX - rect.left - cropArea.x,
                y: e.clientY - rect.top - cropArea.y
            });
        }
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging || !cropImageRef.current) return;
            
            const rect = cropImageRef.current.getBoundingClientRect();
            const x = Math.max(0, Math.min(e.clientX - rect.left - dragStart.x, imageSize.width - cropArea.width));
            const y = Math.max(0, Math.min(e.clientY - rect.top - dragStart.y, imageSize.height - cropArea.height));
            
            setCropArea(prev => ({ ...prev, x, y }));
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragStart, imageSize, cropArea.width, cropArea.height]);

    // Apply crop
    const handleApplyCrop = async () => {
        if (!cropImageRef.current || !cropCanvasRef.current) return;

        const canvas = cropCanvasRef.current;
        const img = cropImageRef.current;
        let imageBitmap = null; // Declare at function scope
        
        try {
            const ctx = canvas.getContext('2d');

            // Calculate actual crop coordinates on original image
            const scaleX = img.naturalWidth / imageSize.width;
            const scaleY = img.naturalHeight / imageSize.height;

            const cropX = cropArea.x * scaleX;
            const cropY = cropArea.y * scaleY;
            const cropWidth = cropArea.width * scaleX;
            const cropHeight = cropArea.height * scaleY;

            canvas.width = cropWidth;
            canvas.height = cropHeight;

            // Clear canvas
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Always use ImageBitmap from blob to avoid CORS issues
            
            if (cropImageData.blob && window.createImageBitmap) {
                try {
                    // Create ImageBitmap from blob - this avoids CORS issues completely
                    imageBitmap = await createImageBitmap(cropImageData.blob, {
                        imageOrientation: 'none',
                        premultiplyAlpha: 'none',
                        colorSpaceConversion: 'none'
                    });
                    
                    // Draw cropped image using ImageBitmap
                    ctx.drawImage(
                        imageBitmap,
                        cropX, cropY, cropWidth, cropHeight,
                        0, 0, cropWidth, cropHeight
                    );
                } catch (bitmapError) {
                    console.error('Failed to create ImageBitmap from blob:', bitmapError);
                    throw new Error('Не удалось обработать изображение. Убедитесь, что изображение загружено корректно.');
                }
            } else {
                // Fallback: try to fetch the image again as blob
                try {
                    const response = await fetch(cropImageData.originalUrl || cropImageData.url);
                    const blob = await response.blob();
                    imageBitmap = await createImageBitmap(blob);
                    
                    ctx.drawImage(
                        imageBitmap,
                        cropX, cropY, cropWidth, cropHeight,
                        0, 0, cropWidth, cropHeight
                    );
                } catch (fetchError) {
                    console.error('Failed to fetch and create ImageBitmap:', fetchError);
                    throw new Error('Не удалось загрузить изображение для обрезки. Проверьте настройки CORS на сервере.');
                }
            }

            // Convert to blob with error handling
            return new Promise((resolve, reject) => {
                try {
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const croppedUrl = URL.createObjectURL(blob);
                            setCropPreview(croppedUrl);

                            // Update image edits with crop data
                            const cropData = {
                                x: cropX,
                                y: cropY,
                                width: cropWidth,
                                height: cropHeight
                            };

                            const originalUrl = cropImageData.originalUrl || cropImageData.url;

                            if (cropImageData.isMain) {
                                setImageEdits(prev => ({
                                    ...prev,
                                    main_image: {
                                        ...prev.main_image,
                                        crop: cropData
                                    }
                                }));
                            } else {
                                setImageEdits(prev => ({
                                    ...prev,
                                    additional_images: prev.additional_images.map(img => {
                                        // Match by original URL
                                        const imgUrl = img.url;
                                        if (imgUrl === originalUrl || imgUrl === cropImageData.url) {
                                            return { ...img, crop: cropData };
                                        }
                                        return img;
                                    }),
                                }));
                            }

                            // Cleanup blob URL if it was created
                            if (cropImageData.url && cropImageData.url.startsWith('blob:')) {
                                URL.revokeObjectURL(cropImageData.url);
                            }

                            // Cleanup ImageBitmap if used
                            if (imageBitmap && imageBitmap.close) {
                                imageBitmap.close();
                            }

                            setCropModalOpen(false);
                            resolve();
                        } else {
                            reject(new Error('Failed to create blob from canvas'));
                        }
                    }, 'image/jpeg', 0.95);
                } catch (error) {
                    reject(error);
                }
            });
        } catch (error) {
            console.error('Error applying crop:', error);
            // Cleanup ImageBitmap if it was created
            if (typeof imageBitmap !== 'undefined' && imageBitmap && imageBitmap.close) {
                imageBitmap.close();
            }
            
            // Check if it's a tainted canvas error
            const errorMessage = error.message || error.toString();
            if (errorMessage.includes('tainted') || errorMessage.includes('Tainted')) {
                alert('Не удалось обрезать изображение из-за ограничений безопасности (CORS). Убедитесь, что сервер разрешает доступ к изображениям с вашего домена. Настройте CORS headers на сервере: Access-Control-Allow-Origin: *');
            } else {
                alert('Ошибка при обрезке изображения: ' + (errorMessage || 'Неизвестная ошибка'));
            }
        }
    };

    // Get image URL with crop applied
    const getImageUrl = (imgData, isMain = false) => {
        const edit = isMain ? imageEdits.main_image : imageEdits.additional_images.find(img => img.url === imgData.url);
        const originalUrl = cropImageData.originalUrl || cropImageData.url;
        
        if (edit?.crop && cropPreview) {
            // Check if this is the cropped image
            if (isMain && cropImageData.isMain) {
                return cropPreview;
            }
            if (!isMain && imgData.url === originalUrl) {
                return cropPreview;
            }
        }
        return imgData.url;
    };

    // Save handler — builds form-data payload per AdminAnnouncementUpdateRequest
    const handleSave = () => {
        if (editableFields.status === "rejected" && !editableFields.reject_message.trim()) {
            alert("При отклонении необходимо указать причину (reject_message).");
            return;
        }
        // image_updates: only is_deleted and rotation (0, 90, 180, 270). No crop in API.
        const mainRotation = imageEdits.main_image.rotation % 360;
        const normalizedMainRotation = [0, 90, 180, 270].reduce((prev, curr) =>
            Math.abs(curr - mainRotation) < Math.abs(prev - mainRotation) ? curr : prev);
        const imageUpdatesToSend = {
            main_image: {
                is_deleted: imageEdits.main_image.is_deleted,
                rotation: normalizedMainRotation,
            },
            additional_images: imageEdits.additional_images.map(img => {
                const rot = img.rotation % 360;
                const normalizedRot = [0, 90, 180, 270].reduce((prev, curr) =>
                    Math.abs(curr - rot) < Math.abs(prev - rot) ? curr : prev);
                return {
                    url: img.url,
                    is_deleted: img.is_deleted,
                    rotation: normalizedRot,
                };
            }),
        };

        const imagesPayload = [];
        if (mainNewImage) imagesPayload.push(mainNewImage);
        if (additionalNewImages?.length) imagesPayload.push(...additionalNewImages);

        const updatedData = {
            status: editableFields.status,
            title: editableFields.title,
            description: editableFields.description,
            image_updates: JSON.stringify(imageUpdatesToSend),
        };

        if (editableFields.status === "rejected" && editableFields.reject_message) {
            updatedData.reject_message = editableFields.reject_message;
        }
        if (editableFields.price !== "") updatedData.price = String(editableFields.price);
        if (editableFields.currency !== "") updatedData.currency = String(editableFields.currency).slice(0, 3);
        if (editableFields.category_id !== "") updatedData.category_id = Number(editableFields.category_id);
        if (editableFields.subcategory_id !== "") updatedData.subcategory_id = Number(editableFields.subcategory_id);
        if (editableFields.user_location_id !== "") updatedData.user_location_id = Number(editableFields.user_location_id);
        if (editableFields.video_url !== "") updatedData.video_url = editableFields.video_url;
        updatedData.is_vip = editableFields.is_vip;
        updatedData.barter = editableFields.barter;
        updatedData.negotiable = editableFields.negotiable;
        if (editableFields.keywords !== "") {
            const kw = editableFields.keywords.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
            if (kw.length) updatedData.keywords = kw;
        }
        if (editableFields.measurement_unit !== "") updatedData.measurement_unit = editableFields.measurement_unit;
        if (imagesPayload.length) updatedData.images = imagesPayload;

        onSave(adData.id, updatedData);
    };

    if (!adData) {
        return (
            <div className="flex justify-center items-center h-48">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <div className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Загрузка деталей объявления...</div>
                </div>
            </div>
        );
    }

    const currentMainRotation = imageEdits.main_image.rotation;
    const isMainDeleted = imageEdits.main_image.is_deleted;
    const allImages = [
        { url: adData.main_image_url, isMain: true, ...imageEdits.main_image },
        ...imageEdits.additional_images.map(img => ({ ...img, isMain: false }))
    ].filter(img => img.url);

    return (
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg`}>
            {/* Header */}
            <div className={`sticky top-0 ${isDark ? 'bg-gray-800' : 'bg-white'} border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} px-6 py-4 z-10`}>
                <div className="flex items-center justify-between">
                    <div className="flex-1">
                {isEditing ? (
                            <Input
                        type="text"
                        value={editableFields.title}
                        onChange={(e) => setEditableFields({ ...editableFields, title: e.target.value })}
                                className="text-2xl font-bold"
                                placeholder="Название объявления"
                    />
                ) : (
                            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {adData.title}
                    </h2>
                )}
                        <div className="flex items-center gap-4 mt-2">
                            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                ID: {adData.id}
                            </span>
                            {isEditing ? (
                                <div className="flex flex-wrap items-center gap-3">
                                    <label className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Статус:</label>
                                    <select
                                        value={editableFields.status}
                                        onChange={(e) => setEditableFields({ ...editableFields, status: e.target.value })}
                                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                    >
                                        <option value="pending">В ожидании</option>
                                        <option value="active">Принято</option>
                                        <option value="rejected">Отклонено</option>
                                    </select>
                                    {editableFields.status === "rejected" && (
                                        <>
                                            <label className={`text-sm font-medium ${isDark ? 'text-red-400' : 'text-red-600'}`}>Причина отклонения:</label>
                                            <input
                                                type="text"
                                                value={editableFields.reject_message}
                                                onChange={(e) => setEditableFields({ ...editableFields, reject_message: e.target.value })}
                                                placeholder="Обязательно при отклонении"
                                                className={`flex-1 min-w-[200px] px-3 py-1.5 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'}`}
                                            />
                                        </>
                                    )}
                                </div>
                            ) : (
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                adData.status === 'pending' ? 'dark-badge-pending' :
                                adData.status === 'active' ? 'dark-badge-success' :
                                adData.status === 'rejected' ? 'dark-badge-danger' :
                                `${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`
                            }`}>
                                {adData.status === 'pending' ? 'В ожидании' : 
                                 adData.status === 'active' ? 'Принято' : 
                                 adData.status === 'rejected' ? 'Отклонено' : adData.status}
                            </span>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {!isEditing && (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-medium rounded-lg hover:from-yellow-600 hover:to-yellow-700 transition-all duration-200 flex items-center gap-2 transform hover:scale-105"
                            >
                                <FaEdit /> Редактировать
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="p-6 space-y-6">
                {/* Main Image Section */}
            {adData.main_image_url && (
                    <div className="relative group">
                        <div className={`relative rounded-xl overflow-hidden ${isDark ? 'bg-gray-900' : 'bg-gray-100'} shadow-lg`}>
                    <img
                                src={getImageUrl({ url: adData.main_image_url }, true)}
                        alt={adData.title}
                        style={{ transform: `rotate(${currentMainRotation}deg)` }}
                                className={`w-full max-h-[500px] object-contain transition-all duration-300 cursor-pointer hover:opacity-90 ${
                                    isMainDeleted ? 'opacity-30 border-4 border-red-500' : ''
                                }`}
                        onError={(e) => { e.target.onerror = null; e.target.src = localPlaceholder; }}
                                onClick={() => {
                                    setSelectedImageIndex(0);
                                    setImageViewerOpen(true);
                                }}
                            />
                            
                            {isMainDeleted && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 rounded-xl">
                                    <span className="text-white text-2xl font-bold">Удалено</span>
                                </div>
                            )}

                    {isEditing && (
                                <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                                onClick={() => handleRotate(adData.main_image_url, true)}
                                        className="p-3 bg-black bg-opacity-70 text-white rounded-full hover:bg-opacity-90 transition-all transform hover:scale-110"
                                        title="Повернуть на 90°"
                            >
                                <FaRedo />
                            </button>
                                    <button
                                        onClick={() => handleOpenCrop(adData.main_image_url, true)}
                                        className="p-3 bg-black bg-opacity-70 text-white rounded-full hover:bg-opacity-90 transition-all transform hover:scale-110"
                                        title="Обрезать изображение"
                                    >
                                        <FaCrop />
                            </button>
                            <button
                                onClick={() => handleDeleteToggle(adData.main_image_url, true)}
                                        className={`p-3 rounded-full transition-all transform hover:scale-110 ${
                                            isMainDeleted 
                                                ? 'bg-green-600 text-white hover:bg-green-700' 
                                                : 'bg-red-600 text-white hover:bg-red-700'
                                        }`}
                                        title={isMainDeleted ? "Восстановить" : "Удалить"}
                            >
                                <FaTrash />
                            </button>
                        </div>
                    )}
                        </div>
                </div>
            )}

                {/* Additional Images Gallery */}
            {imageEdits.additional_images.length > 0 && (
                    <div>
                        <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            <FaImage className="text-blue-500" />
                            Дополнительные фото ({imageEdits.additional_images.length})
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {imageEdits.additional_images.map((imgEdit, index) => (
                                <div key={imgEdit.url} className="relative group">
                                    <div className={`relative rounded-lg overflow-hidden ${isDark ? 'bg-gray-900' : 'bg-gray-100'} aspect-square shadow-md`}>
                                <img
                                            src={getImageUrl(imgEdit, false)}
                                    alt={`Дополнительное изображение ${index + 1}`}
                                    style={{ transform: `rotate(${imgEdit.rotation}deg)` }}
                                            className={`w-full h-full object-cover transition-all duration-300 cursor-pointer hover:opacity-90 ${
                                                imgEdit.is_deleted ? 'opacity-30 border-2 border-red-500' : ''
                                            }`}
                                    onError={(e) => { e.target.onerror = null; e.target.src = localPlaceholder; }}
                                            onClick={() => {
                                                setSelectedImageIndex(index + 1);
                                                setImageViewerOpen(true);
                                            }}
                                        />
                                        
                                        {imgEdit.is_deleted && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 rounded-lg">
                                                <span className="text-white text-sm font-bold">Удалено</span>
                                            </div>
                                        )}

                                {isEditing && (
                                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => handleRotate(imgEdit.url, false)}
                                                    className="p-2 bg-black bg-opacity-70 text-white rounded-full hover:bg-opacity-90 transition-all text-xs"
                                                    title="Повернуть"
                                                >
                                                    <FaRedo />
                                                </button>
                                                <button
                                                    onClick={() => handleOpenCrop(imgEdit.url, false)}
                                                    className="p-2 bg-black bg-opacity-70 text-white rounded-full hover:bg-opacity-90 transition-all text-xs"
                                                    title="Обрезать"
                                                >
                                                    <FaCrop />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteToggle(imgEdit.url, false)}
                                                    className={`p-2 rounded-full transition-all text-xs ${
                                                        imgEdit.is_deleted 
                                                            ? 'bg-green-600 text-white hover:bg-green-700' 
                                                            : 'bg-red-600 text-white hover:bg-red-700'
                                                    }`}
                                                    title={imgEdit.is_deleted ? "Восстановить" : "Удалить"}
                                                >
                                                    <FaTrash />
                                        </button>
                                    </div>
                                )}
                                    </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

                {/* Upload New Images (Edit Mode) */}
            {isEditing && (
                    <div className={`p-6 rounded-xl border-2 border-dashed ${isDark ? 'border-gray-600 bg-gray-900' : 'border-gray-300 bg-gray-50'} space-y-4`}>
                        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Загрузка новых изображений
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Основное фото
                                </label>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                        onClick={() => mainFileInputRef.current?.click()}
                                        className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all flex items-center gap-2"
                            >
                                        <FaImage /> Выбрать файл
                            </button>
                            {mainNewImage && (
                                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                            {mainNewImage.name}
                                        </span>
                            )}
                        </div>
                        <input
                            ref={mainFileInputRef}
                            type="file"
                            accept="image/*"
                                    onChange={(e) => setMainNewImage(e.target.files?.[0] || null)}
                            className="hidden"
                        />
                    </div>
                    <div>
                                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Дополнительные фото
                                </label>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                        onClick={() => additionalFilesInputRef.current?.click()}
                                        className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-medium rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all flex items-center gap-2"
                            >
                                        <FaImage /> Выбрать файлы
                            </button>
                            {additionalNewImages.length > 0 && (
                                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                            {additionalNewImages.length} файл(ов)
                                        </span>
                            )}
                        </div>
                        <input
                            ref={additionalFilesInputRef}
                            type="file"
                            multiple
                            accept="image/*"
                                    onChange={(e) => setAdditionalNewImages(Array.from(e.target.files || []))}
                            className="hidden"
                        />
                            </div>
                    </div>
                </div>
            )}

                {/* Description Section */}
                <div>
                    <h3 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Описание
                    </h3>
                {isEditing ? (
                        <Textarea
                        value={editableFields.description}
                        onChange={(e) => setEditableFields({ ...editableFields, description: e.target.value })}
                            className="w-full min-h-[150px]"
                            placeholder="Введите описание объявления..."
                    />
                ) : (
                        <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-900 text-gray-300' : 'bg-gray-50 text-gray-700'} whitespace-pre-wrap leading-relaxed`}>
                            {adData.description || "Описание отсутствует."}
                        </div>
                )}
            </div>

                {/* Admin form fields (only when editing) */}
                {isEditing && (
                    <div className={`p-6 rounded-xl border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Поля объявления (form-data)</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Цена</label>
                                <Input type="text" value={editableFields.price} onChange={(e) => setEditableFields({ ...editableFields, price: e.target.value })} placeholder="Число" />
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Валюта (до 3 символов)</label>
                                <Input type="text" value={editableFields.currency} onChange={(e) => setEditableFields({ ...editableFields, currency: e.target.value.slice(0, 3) })} placeholder="KZT" maxLength={3} />
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Категория</label>
                                <select
                                    value={editableFields.category_id}
                                    onChange={(e) => setEditableFields({ ...editableFields, category_id: e.target.value, subcategory_id: "" })}
                                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                >
                                    <option value="">—</option>
                                    {(categories || []).map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Подкатегория</label>
                                <select
                                    value={editableFields.subcategory_id}
                                    onChange={(e) => setEditableFields({ ...editableFields, subcategory_id: e.target.value })}
                                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                >
                                    <option value="">—</option>
                                    {(() => {
                                        const cat = (categories || []).find(c => c.id === Number(editableFields.category_id));
                                        return (cat?.subcategories || []).map(sub => <option key={sub.id} value={sub.id}>{sub.name}</option>);
                                    })()}
                                </select>
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>ID локации пользователя</label>
                                <Input type="number" value={editableFields.user_location_id} onChange={(e) => setEditableFields({ ...editableFields, user_location_id: e.target.value })} placeholder="user_location_id" />
                            </div>
                            <div className="sm:col-span-2">
                                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Ссылка на видео</label>
                                <Input type="url" value={editableFields.video_url} onChange={(e) => setEditableFields({ ...editableFields, video_url: e.target.value })} placeholder="https://..." />
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Единица измерения</label>
                                <select
                                    value={editableFields.measurement_unit}
                                    onChange={(e) => setEditableFields({ ...editableFields, measurement_unit: e.target.value })}
                                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                >
                                    <option value="">—</option>
                                    <option value="piece">шт (piece)</option>
                                    <option value="kg">кг (kg)</option>
                                    <option value="ton">тонна (ton)</option>
                                    <option value="liter">литр (liter)</option>
                                    <option value="meter">метр (meter)</option>
                                    <option value="sq_meter">м² (sq_meter)</option>
                                    <option value="hectare">га (hectare)</option>
                                    <option value="pack">упаковка (pack)</option>
                                    <option value="box">ящик (box)</option>
                                    <option value="other">другое (other)</option>
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Ключевые слова (через запятую)</label>
                                <Input type="text" value={editableFields.keywords} onChange={(e) => setEditableFields({ ...editableFields, keywords: e.target.value })} placeholder="тег1, тег2, тег3" />
                            </div>
                            <div className="flex flex-wrap items-center gap-6">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={editableFields.is_vip} onChange={(e) => setEditableFields({ ...editableFields, is_vip: e.target.checked })} className="rounded text-blue-500" />
                                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>VIP</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={editableFields.barter} onChange={(e) => setEditableFields({ ...editableFields, barter: e.target.checked })} className="rounded text-blue-500" />
                                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Обмен</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={editableFields.negotiable} onChange={(e) => setEditableFields({ ...editableFields, negotiable: e.target.checked })} className="rounded text-blue-500" />
                                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Цена договорная</span>
                                </label>
                            </div>
                        </div>
                    </div>
                )}

                {/* Details Grid */}
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 p-6 rounded-xl ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                    <div className="flex items-start gap-3">
                        <div className={`p-3 rounded-lg ${isDark ? 'bg-blue-900' : 'bg-blue-100'}`}>
                            <FaTag className={`text-lg ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                        </div>
                        <div className="flex-1">
                            <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Цена</div>
                            <div className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatListingPrice(adData.price, adData.currency, adData.negotiable)}
                        {adData.negotiable && adData.price != null && adData.price !== "None" && adData.price !== "" && (
                            <span className={`ml-2 text-sm font-normal ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>(договорная)</span>
                        )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-start gap-3">
                        <div className={`p-3 rounded-lg ${isDark ? 'bg-purple-900' : 'bg-purple-100'}`}>
                            <FaTag className={`text-lg ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                        </div>
                        <div className="flex-1">
                            <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Категория</div>
                            <div className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {getCategoryName(adData.category_id)}
                            </div>
                            {adData.subcategory_id && (
                                <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {getSubcategoryName(adData.category_id, adData.subcategory_id)}
                                </div>
                            )}
                        </div>
                    </div>

                    {adData.location && (
                        <div className="flex items-start gap-3">
                            <div className={`p-3 rounded-lg ${isDark ? 'bg-green-900' : 'bg-green-100'}`}>
                                <FaMapMarkerAlt className={`text-lg ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                            </div>
                            <div className="flex-1">
                                <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Локация</div>
                                <div className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {adData.location.locality || 'Не указано'}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex items-start gap-3">
                        <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                            <FaCalendarAlt className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                        </div>
                        <div className="flex-1">
                            <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Создано</div>
                            <div className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {new Date(adData.created_at).toLocaleDateString('ru-RU', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                            </div>
                        </div>
                    </div>
            </div>

                {/* Action Buttons */}
                {isEditing && (
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                        <button
                            onClick={() => setIsEditing(false)}
                            className="px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 transition-all flex items-center gap-2"
                        >
                            <FaTimes /> Отмена
                        </button>
                        <button
                            onClick={handleSave}
                            className="px-6 py-3 bg-gradient-to-r from-emerald-400 to-emerald-500 text-emerald-950 font-semibold rounded-lg hover:from-emerald-500 hover:to-emerald-600 transition-all flex items-center gap-2 transform hover:scale-105"
                        >
                            <FaSave /> Сохранить изменения
                        </button>
                    </div>
                )}
            </div>

            {/* Image Viewer Modal */}
            {imageViewerOpen && selectedImageIndex !== null && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
                    onClick={() => setImageViewerOpen(false)}
                >
                    <button
                        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
                        onClick={() => setImageViewerOpen(false)}
                    >
                        <FaTimes className="w-8 h-8" />
                    </button>
                    <div className="max-w-7xl max-h-[90vh] flex items-center justify-center">
                        <img
                            src={allImages[selectedImageIndex]?.url}
                            alt={`Изображение ${selectedImageIndex + 1}`}
                            className="max-w-full max-h-[90vh] object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    {allImages.length > 1 && (
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : allImages.length - 1));
                                }}
                                className="px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30"
                            >
                                ← Предыдущее
                            </button>
                            <span className="px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg">
                                {selectedImageIndex + 1} / {allImages.length}
                            </span>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedImageIndex((prev) => (prev < allImages.length - 1 ? prev + 1 : 0));
                                }}
                                className="px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30"
                            >
                                Следующее →
                        </button>
                        </div>
                    )}
                </div>
            )}

            {/* Crop Modal */}
            {cropModalOpen && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-90 z-[60] flex items-center justify-center p-4"
                    onClick={() => setCropModalOpen(false)}
                >
                    <div 
                        className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-auto custom-scrollbar`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                Обрезка изображения
                            </h3>
                    <button
                                onClick={() => setCropModalOpen(false)}
                                className={`p-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors rounded-full hover:bg-opacity-20`}
                    >
                                <FaTimes className="w-6 h-6" />
                        </button>
                        </div>

                        {/* Debug info */}
                        <div className={`mb-2 p-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded text-xs`}>
                            <div>URL: {cropImageData.url ? 'Available' : 'Missing'}</div>
                            <div>Blob: {cropImageData.blob ? 'Available' : 'Missing'}</div>
                            <div>Image Size: {imageSize.width} x {imageSize.height}</div>
                            <div>Crop Area: {Math.round(cropArea.width)} x {Math.round(cropArea.height)}</div>
                        </div>

                        <div className="relative mb-4 flex justify-center" style={{ minHeight: '400px' }}>
                            <div className="relative inline-block">
                                {cropImageData.url ? (
                                    <>
                                        <img
                                            ref={cropImageRef}
                                            key={cropImageData.url}
                                            src={cropImageData.url}
                                            alt="Crop"
                                            style={{ 
                                                display: 'block', 
                                                maxWidth: '800px',
                                                maxHeight: '500px',
                                                width: 'auto',
                                                height: 'auto',
                                                border: '2px solid red' // Debug border
                                            }}
                                            onLoad={(e) => {
                                                console.log('Image loaded!', e.target.naturalWidth, 'x', e.target.naturalHeight);
                                                const img = cropImageRef.current;
                                                if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
                                                    const maxWidth = 800;
                                                    const maxHeight = 500;
                                                    let width = img.naturalWidth;
                                                    let height = img.naturalHeight;
                                                    
                                                    if (width > maxWidth || height > maxHeight) {
                                                        const ratio = Math.min(maxWidth / width, maxHeight / height);
                                                        width = width * ratio;
                                                        height = height * ratio;
                                                    }
                                                    
                                                    console.log('Setting image size:', width, 'x', height);
                                                    setImageSize({ width, height });
                                                    setCropArea({
                                                        x: width * 0.1,
                                                        y: height * 0.1,
                                                        width: width * 0.8,
                                                        height: height * 0.8
                                                    });
                                                }
                                            }}
                                            onError={(e) => {
                                                console.error('Crop image failed to load:', cropImageData.url, e);
                                                if (cropImageData.originalUrl && cropImageData.originalUrl !== cropImageData.url) {
                                                    console.log('Trying original URL:', cropImageData.originalUrl);
                                                    e.target.src = cropImageData.originalUrl;
                                                }
                                            }}
                                        />
                                        {imageSize.width === 0 && (
                                            <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-gray-900 bg-opacity-70' : 'bg-gray-100 bg-opacity-70'} rounded-lg`}>
                                                <div className="text-center">
                                                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                                                    <div className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Загрузка изображения...</div>
                                                </div>
                                            </div>
                                        )}
                    </>
                ) : (
                                    <div className={`flex items-center justify-center w-full h-64 ${isDark ? 'bg-gray-900' : 'bg-gray-100'} rounded-lg`}>
                                        <div className="text-center">
                                            <div className={`${isDark ? 'text-red-300' : 'text-red-600'}`}>URL отсутствует</div>
                                        </div>
                                    </div>
                                )}
                                {imageSize.width > 0 && imageSize.height > 0 && cropArea.width > 0 && cropArea.height > 0 && (
                                    <>
                                        <div
                                            className="absolute border-2 border-blue-500 bg-blue-500 bg-opacity-20 cursor-move z-10"
                                            style={{
                                                left: `${cropArea.x}px`,
                                                top: `${cropArea.y}px`,
                                                width: `${cropArea.width}px`,
                                                height: `${cropArea.height}px`
                                            }}
                                            onMouseDown={handleCropMouseDown}
                                        />
                                        <div className="absolute inset-0 pointer-events-none">
                                            <div 
                                                className="absolute bg-black bg-opacity-50"
                                                style={{
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    height: `${cropArea.y}px`
                                                }}
                                            />
                                            <div 
                                                className="absolute bg-black bg-opacity-50"
                                                style={{
                                                    top: `${cropArea.y}px`,
                                                    left: 0,
                                                    width: `${cropArea.x}px`,
                                                    height: `${cropArea.height}px`
                                                }}
                                            />
                                            <div 
                                                className="absolute bg-black bg-opacity-50"
                                                style={{
                                                    top: `${cropArea.y + cropArea.height}px`,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0
                                                }}
                                            />
                                            <div 
                                                className="absolute bg-black bg-opacity-50"
                                                style={{
                                                    top: `${cropArea.y}px`,
                                                    left: `${cropArea.x + cropArea.width}px`,
                                                    right: 0,
                                                    height: `${cropArea.height}px`
                                                }}
                                            />
                                        </div>
                                    </>
                )}
            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Позиция X: {Math.round(cropArea.x)}
                                </label>
                                <input
                                    type="range"
                                    min="0"
                                    max={Math.max(0, imageSize.width - cropArea.width)}
                                    value={cropArea.x}
                                    onChange={(e) => setCropArea(prev => ({ ...prev, x: parseInt(e.target.value) }))}
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Позиция Y: {Math.round(cropArea.y)}
                                </label>
                                <input
                                    type="range"
                                    min="0"
                                    max={Math.max(0, imageSize.height - cropArea.height)}
                                    value={cropArea.y}
                                    onChange={(e) => setCropArea(prev => ({ ...prev, y: parseInt(e.target.value) }))}
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Ширина: {Math.round(cropArea.width)}
                                </label>
                                <input
                                    type="range"
                                    min="50"
                                    max={imageSize.width}
                                    value={cropArea.width}
                                    onChange={(e) => {
                                        const newWidth = parseInt(e.target.value);
                                        setCropArea(prev => ({
                                            ...prev,
                                            width: newWidth,
                                            x: Math.min(prev.x, imageSize.width - newWidth)
                                        }));
                                    }}
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Высота: {Math.round(cropArea.height)}
                                </label>
                                <input
                                    type="range"
                                    min="50"
                                    max={imageSize.height}
                                    value={cropArea.height}
                                    onChange={(e) => {
                                        const newHeight = parseInt(e.target.value);
                                        setCropArea(prev => ({
                                            ...prev,
                                            height: newHeight,
                                            y: Math.min(prev.y, imageSize.height - newHeight)
                                        }));
                                    }}
                                    className="w-full"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setCropModalOpen(false)}
                                className="px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 transition-all"
                            >
                                Отмена
                            </button>
                            <button
                                onClick={handleApplyCrop}
                                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all"
                            >
                                Применить обрезку
                            </button>
                        </div>

                        <canvas ref={cropCanvasRef} className="hidden" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdDetailsModalContent;

